import React, { useMemo, useState } from 'react';
import {
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardHeader,
  CardBody,
  SortDescriptor,
} from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { formatMoneyUSD, formatNumber } from '../../utils/functions';
import {
  groupSettlementsByShip,
  sortWeekKeys,
} from 'common/src/utils/functions';
import { UNIFIED_TABLE_CLASSNAMES } from '../../utils/table-styles';
import InformationTooltip from '../../../../common/src/components/ui/InformationTooltip';
import * as XLSX from 'xlsx';
import ExcelExportButton from '../ui/ExcelExportButton';
import useDataStore from '../../../../common/src/stores/useDataStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

interface Props {
  settlements: Settlement[];
}

interface ClientSaldo {
  positive: number;
  negative: number;
  total: number;
  totalKilos: number;
  comparableKilos: number;
  details: {
    [key: string]: {
      kilos: number;
      fob: number;
      fobPerKg: number;
      difference: number;
      totalDifference: number;
      comparisonData: {
        kilos: number;
        fob: number;
        fobPerKg: number;
      };
    };
  };
  shipTotals: {
    [shipKey: string]: {
      positive: number;
      negative: number;
      total: number;
      totalKilos: number;
      comparableKilos: number;
      details: {
        [detailKey: string]: {
          kilos: number;
          fob: number;
          fobPerKg: number;
          difference: number;
          totalDifference: number;
          comparisonData: {
            kilos: number;
            fob: number;
            fobPerKg: number;
          };
        };
      };
    };
  };
}

// Small money cell. Positive = green, negative = red, zero = muted.
const ValueDisplay = ({
  value,
  size = 'md',
}: {
  value: number | null;
  size?: 'sm' | 'md';
}) => {
  if (value === null) return <span className='text-default-300'>N/A</span>;

  const className =
    value > 0
      ? 'text-green-600 dark:text-green-400'
      : value < 0
        ? 'text-red-600 dark:text-red-400'
        : 'text-default-500';

  return (
    <span
      className={`${className} tabular-nums ${size === 'sm' ? 'text-xs' : 'text-sm'}`}
    >
      {formatMoneyUSD(value, { decimals: 0 })}
    </span>
  );
};

// SALDO pill — the headline metric of this table. Renders the saldo as a
// rounded green/red chip so it pops vs. the surrounding plain-text columns.
// Matches the visual language of the percentage pills in Desempeño Comercial.
const SaldoPill = ({ value }: { value: number | null }) => {
  if (value === null) return <span className='text-default-300'>N/A</span>;

  const isPositive = value > 0;
  const isZero = value === 0;
  const pillClass = isZero
    ? 'bg-default-100 text-default-600 dark:bg-default-50 dark:text-default-400'
    : isPositive
      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
      : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  const sign = isPositive ? '+' : '';
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-bold tabular-nums ${pillClass}`}
    >
      {sign}
      {formatMoneyUSD(value, { decimals: 0 })}
    </span>
  );
};

// Volume and FOB share the same "big number + muted % secondary line,
// right-aligned" styling, mirroring Desempeño Comercial for consistency.
const VolumeCell = ({
  kilos,
  grandTotalKilos,
}: {
  kilos: number;
  grandTotalKilos: number;
}) => {
  const pct = grandTotalKilos > 0 ? (kilos / grandTotalKilos) * 100 : 0;
  return (
    <div className='flex flex-col items-end'>
      <span className='font-semibold tabular-nums text-sm'>
        {formatNumber(kilos, { decimals: 0 })}
      </span>
      <span className='text-[10px] text-default-400 tabular-nums'>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
};

const FobCell = ({
  fob,
  grandTotalFob,
}: {
  fob: number;
  grandTotalFob: number;
}) => {
  const pct = grandTotalFob > 0 ? (fob / grandTotalFob) * 100 : 0;
  return (
    <div className='flex flex-col items-end'>
      <span className='font-semibold tabular-nums text-sm'>
        {formatMoneyUSD(fob, { decimals: 0 })}
      </span>
      <span className='text-[10px] text-default-400 tabular-nums'>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
};

// % Comparable rendered as a thin progress bar. 0-100 scale, number next to bar.
const ComparableCell = ({ value }: { value: number }) => {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className='flex items-center gap-2 min-w-[90px]'>
      <div className='flex-1 h-1.5 bg-default-100 dark:bg-default-50 rounded-full overflow-hidden'>
        <div
          className={`h-full rounded-full ${pct >= 75 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className='text-[11px] text-default-500 tabular-nums w-8 text-right'>
        {pct.toFixed(0)}%
      </span>
    </div>
  );
};

const ClientComparativeSaldosTable: React.FC<Props> = ({ settlements }) => {
  const { t } = useTranslation('settlements');
  const { selectedSpecies } = useDataStore();
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'total',
    direction: 'ascending',
  });

  const [visibleCount, setVisibleCount] = useState(10);

  const calculateComparativeSaldo = useMemo(() => {
    if (!settlements || settlements.length === 0) return {};

    // Solo procesar settlements que tengan freight y exchange rate
    const validSettlements = settlements.filter(
      (settlement) =>
        settlement.total_freight_usd !== null &&
        settlement.total_freight_usd !== undefined &&
        settlement.total_freight_usd > 0 &&
        settlement.exchange_rate !== null &&
        settlement.exchange_rate !== undefined
    );

    const byShips = groupSettlementsByShip(validSettlements, undefined, { includeMarketInAir: true });
    const clientSaldos: Record<string, ClientSaldo> = {};

    // Procesar cada nave
    Object.entries(byShips).forEach(([shipKey, shipSettlements]) => {
      // Agrupar detalles por cliente dentro de la nave
      const clientDetails: Record<
        string,
        Record<string, { kilos: number; fob: number }>
      > = {};

      // 1. Recolectar todos los detalles por cliente para esta nave
      shipSettlements.forEach((settlement) => {
        const client = settlement.receiver;
        if (!clientDetails[client]) {
          clientDetails[client] = {};
        }

        settlement.settlement_details?.forEach((detail) => {
          if (!detail.variety || !detail.caliber) return;
          // Si la especie requiere color, verificamos que exista; si no requiere color, no lo verificamos
          if (selectedSpecies?.has_color && !detail.color) return;

          // Construir detailKey basado en si la especie tiene color o no
          const detailKey = selectedSpecies?.has_color
            ? `${detail.variety}-${detail.caliber}-${detail.color}-${detail.format}-${detail.unit_weight}KG`
            : `${detail.variety}-${detail.caliber}-${detail.format}-${detail.unit_weight}KG`;

          if (!clientDetails[client][detailKey]) {
            clientDetails[client][detailKey] = {
              kilos: 0,
              fob: 0,
            };
          }

          // Sumar kilos y FOB sin redondear para mantener precisión
          clientDetails[client][detailKey].kilos += detail.kilos;
          clientDetails[client][detailKey].fob += detail.usd_total_fob;
        });
      });

      // 2. Calcular diferencias para cada cliente y detalle dentro de la nave
      Object.entries(clientDetails).forEach(([client, details]) => {
        if (!clientSaldos[client]) {
          clientSaldos[client] = {
            positive: 0,
            negative: 0,
            total: 0,
            totalKilos: 0,
            comparableKilos: 0,
            details: {},
            shipTotals: {},
          };
        }

        // Inicializar totales para esta nave
        if (!clientSaldos[client].shipTotals[shipKey]) {
          clientSaldos[client].shipTotals[shipKey] = {
            positive: 0,
            negative: 0,
            total: 0,
            totalKilos: 0,
            comparableKilos: 0,
            details: {},
          };
        }

        // Para cada detalle del cliente principal
        Object.entries(details).forEach(([detailKey, mainDetail]) => {
          // Actualizar totales de kilos para el cliente
          clientSaldos[client].totalKilos += mainDetail.kilos;
          clientSaldos[client].shipTotals[shipKey].totalKilos +=
            mainDetail.kilos;

          // Buscar el mismo detalle en los clientes de comparación
          let othersTotalKilos = 0;
          let othersTotalFob = 0;

          Object.entries(clientDetails).forEach(
            ([otherClient, otherDetails]) => {
              if (otherClient !== client && otherDetails[detailKey]) {
                othersTotalKilos += otherDetails[detailKey].kilos;
                othersTotalFob += otherDetails[detailKey].fob;
              }
            }
          );

          // Calcular diferencia si hay datos en ambos lados
          if (mainDetail.kilos > 0 && othersTotalKilos > 0) {
            // Actualizar kilos comparables
            clientSaldos[client].comparableKilos += mainDetail.kilos;
            clientSaldos[client].shipTotals[shipKey].comparableKilos +=
              mainDetail.kilos;

            // 1. Calcular FOB/KG para cada grupo (sin redondeo intermedio)
            const mainFobPerKg = mainDetail.fob / mainDetail.kilos;
            const othersFobPerKg = othersTotalFob / othersTotalKilos;

            // 2. Calcular la diferencia por kilo (sin redondeo intermedio)
            const diffFobPerKg = mainFobPerKg - othersFobPerKg;

            // 3. Calcular la diferencia total (solo redondeo final)
            const totalDifference = Number(
              (diffFobPerKg * mainDetail.kilos).toFixed(2)
            );

            // 4. Actualizar saldos
            if (totalDifference > 0) {
              clientSaldos[client].positive += totalDifference;
              clientSaldos[client].shipTotals[shipKey].positive +=
                totalDifference;
            } else {
              clientSaldos[client].negative += totalDifference;
              clientSaldos[client].shipTotals[shipKey].negative +=
                totalDifference;
            }

            clientSaldos[client].total =
              clientSaldos[client].positive + clientSaldos[client].negative;
            clientSaldos[client].shipTotals[shipKey].total =
              clientSaldos[client].shipTotals[shipKey].positive +
              clientSaldos[client].shipTotals[shipKey].negative;

            // 5. Guardar detalles para el Excel
            if (!clientSaldos[client].details[detailKey]) {
              clientSaldos[client].details[detailKey] = {
                kilos: mainDetail.kilos,
                fob: mainDetail.fob,
                fobPerKg: mainFobPerKg,
                difference: diffFobPerKg,
                totalDifference: totalDifference,
                comparisonData: {
                  kilos: othersTotalKilos,
                  fob: othersTotalFob,
                  fobPerKg: othersFobPerKg,
                },
              };
            }

            // Guardar en detalles por nave
            clientSaldos[client].shipTotals[shipKey].details[detailKey] = {
              kilos: mainDetail.kilos,
              fob: mainDetail.fob,
              fobPerKg: mainFobPerKg,
              difference: diffFobPerKg,
              totalDifference: totalDifference,
              comparisonData: {
                kilos: othersTotalKilos,
                fob: othersTotalFob,
                fobPerKg: othersFobPerKg,
              },
            };
          }
        });
      });
    });

    return clientSaldos;
  }, [settlements, selectedSpecies]);

  const exportToExcel = () => {
    const saldos = calculateComparativeSaldo;
    const excelData: any[][] = [];

    // Encabezados
    const headers = [
      'Nave',
      'Cliente',
      'Variedad',
      'Calibre',
      'Color',
      'Formato',
      'Peso Unitario',
      'Kilos',
      'FOB Total',
      'FOB/KG',
      'Kilos Otros',
      'FOB Otros',
      'FOB/KG Otros',
      'Diferencia FOB/KG',
      'Diferencia Total',
      '% Comparable',
    ];
    excelData.push(headers);

    // Agrupar por nave
    const byShips = groupSettlementsByShip(settlements, undefined, { includeMarketInAir: true });

    // Build market name lookup from settlements
    const marketNameMap: Record<string, string> = {};
    settlements.forEach((s) => {
      if (s.market_id && s.market?.name) {
        marketNameMap[s.market_id] = s.market.name;
      }
    });

    // Separar y ordenar aéreos y marítimos
    const shipEntries = Object.entries(byShips);
    const aereos = shipEntries
      .filter(([key]) => key.startsWith('AIR-'))
      // La clave lleva el año ISO (AIR-2026-W53): de la más reciente a la más antigua
      .sort((a, b) => sortWeekKeys(b[0], a[0]));

    const terrestres = shipEntries
      .filter(([key]) => key.startsWith('GROUND-'))
      // La clave lleva el año ISO (AIR-2026-W53): de la más reciente a la más antigua
      .sort((a, b) => sortWeekKeys(b[0], a[0]));
    const maritimos = shipEntries
      .filter(([key]) => key.startsWith('SHIP-'))
      .sort((a, b) => {
        const dateA = new Date(a[0].split('-')[2]);
        const dateB = new Date(b[0].split('-')[2]);
        return dateA.getTime() - dateB.getTime();
      });

    // Procesar primero aéreos y luego marítimos
    [...aereos, ...terrestres, ...maritimos].forEach(([shipKey, _]) => {
      const shipName = shipKey.startsWith('AIR-')
        ? (() => {
            const week = shipKey.match(/-W(\d+)/)?.[1] || '';
            const marketId = shipKey.match(/-M(.+)$/)?.[1] || '';
            const marketName = marketNameMap[marketId] || marketId;
            return `AEREO - Semana ${week} - ${marketName}`;
          })()
        : shipKey.startsWith('GROUND-')
          ? `TERRESTRE - Semana ${shipKey.split('-W')[1]}`
          : shipKey.split('-')[1].replace(/_/g, ' ');

      let hasDataForShip = false;

      // Procesar cada cliente que tenga datos para esta nave
      Object.entries(saldos).forEach(([client, data]) => {
        const shipData = data.shipTotals[shipKey];
        if (shipData && Object.keys(shipData.details).length > 0) {
          if (!hasDataForShip) {
            excelData.push([
              shipName,
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
            ]);
            hasDataForShip = true;
          }

          // Agregar detalles
          Object.entries(shipData.details).forEach(([detailKey, detail]) => {
            // Separar detailKey en sus componentes considerando si hay color o no
            const detailParts = detailKey.split('-');
            let variety, caliber, color, format, unit_weight;

            if (selectedSpecies?.has_color) {
              // Si tiene color, tenemos 4 partes: variety-caliber-color-format
              [variety, caliber, color, format, unit_weight] = detailParts;
            } else {
              // Si no tiene color, solo tenemos 3 partes: variety-caliber-format
              [variety, caliber, format, unit_weight] = detailParts;
              color = ''; // Color vacío si no aplica
            }

            // Calculate the comparable percentage for this product
            const totalKilos = detail.kilos;
            const comparableKilos =
              detail.comparisonData.kilos > 0 ? detail.kilos : 0;
            const comparablePercentage =
              comparableKilos > 0 ? (comparableKilos / totalKilos) * 100 : 0;

            const row = [
              shipName,
              client,
              variety,
              caliber,
              color,
              format,
              unit_weight,
              Number(detail.kilos.toFixed(2)),
              Number(detail.fob.toFixed(2)),
              Number(detail.fobPerKg.toFixed(2)),
              Number(detail.comparisonData.kilos.toFixed(2)),
              Number(detail.comparisonData.fob.toFixed(2)),
              Number(detail.comparisonData.fobPerKg.toFixed(2)),
              Number(detail.difference.toFixed(2)),
              Number(detail.totalDifference.toFixed(2)),
              `${Number(comparablePercentage.toFixed(0))}%`,
            ];
            excelData.push(row);
          });

          // Agregar total de la nave
          if (Math.abs(shipData.total) > 0.01) {
            // Calculate ship comparable percentage
            const shipComparablePercentage =
              shipData.totalKilos > 0
                ? (shipData.comparableKilos / shipData.totalKilos) * 100
                : 0;

            // Calculate saldo per kg for this ship
            const shipSaldoPerKg =
              shipData.comparableKilos > 0
                ? Number((shipData.total / shipData.comparableKilos).toFixed(2))
                : null;

            excelData.push([
              shipName,
              'TOTAL ' + client,
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              Number(shipData.total.toFixed(2)),
              shipSaldoPerKg !== null ? shipSaldoPerKg : 'N/A',
              `${Number(shipComparablePercentage.toFixed(0))}%`,
            ]);
          }
        }
      });

      if (hasDataForShip) {
        excelData.push(Array(headers.length).fill(''));
      }
    });

    // Agregar resumen final
    excelData.push(Array(headers.length).fill(''));
    excelData.push([
      'RESUMEN FINAL',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
    ]);
    excelData.push([
      'Cliente',
      'Positivo',
      'Negativo',
      'Saldo',
      'Saldo/KG',
      '% Comparable',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
    ]);

    Object.entries(saldos)
      .filter(([_, data]) =>
        Object.values(data.details).some(
          (detail) => detail.comparisonData.kilos > 0
        )
      )
      .forEach(([client, data]) => {
        const comparablePercentage =
          data.totalKilos > 0
            ? (data.comparableKilos / data.totalKilos) * 100
            : 0;
        const saldoPerKg =
          data.comparableKilos > 0
            ? Number((data.total / data.comparableKilos).toFixed(2))
            : null;

        excelData.push([
          client,
          Number(data.positive.toFixed(2)),
          Number(data.negative.toFixed(2)),
          Number(data.total.toFixed(2)),
          saldoPerKg !== null ? Number(saldoPerKg) : 'N/A',
          `${Number(comparablePercentage.toFixed(0))}%`,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ]);
      });

    // Crear y configurar el libro Excel
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Estilos
    const headerStyle = {
      fill: { fgColor: { rgb: '4F81BD' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true },
      alignment: { horizontal: 'center' },
    };

    const shipRowStyle = {
      fill: { fgColor: { rgb: '8EA9DB' } },
      font: { bold: true },
    };

    const totalRowStyle = {
      fill: { fgColor: { rgb: 'E2EFDA' } },
      font: { bold: true },
    };

    const positiveValueStyle = {
      font: { color: { rgb: '008000' } },
    };

    const negativeValueStyle = {
      font: { color: { rgb: 'FF0000' } },
    };

    const resumenFinalStyle = {
      fill: { fgColor: { rgb: '4F81BD' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true },
    };

    // Aplicar estilos
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

    // Estilo para encabezados
    for (let C = 0; C <= range.e.c; ++C) {
      const cell = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[cell]) ws[cell] = { v: '' };
      ws[cell].s = headerStyle;
    }

    // Estilos para datos
    for (let R = 1; R <= range.e.r; ++R) {
      for (let C = 0; C <= range.e.c; ++C) {
        const cell = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cell]) continue;

        // Detectar filas de naves (solo tienen el nombre de la nave)
        if (
          C === 0 &&
          ws[cell].v &&
          typeof ws[cell].v === 'string' &&
          ws[XLSX.utils.encode_cell({ r: R, c: 1 })].v === ''
        ) {
          ws[cell].s = shipRowStyle;
        }

        // Detectar filas de totales
        if (
          ws[cell].v &&
          typeof ws[cell].v === 'string' &&
          ws[cell].v.startsWith('TOTAL')
        ) {
          ws[cell].s = totalRowStyle;
        }

        // Detectar resumen final
        if (
          ws[cell].v &&
          typeof ws[cell].v === 'string' &&
          (ws[cell].v === 'RESUMEN FINAL' ||
            ws[cell].v === 'Cliente' ||
            ws[cell].v === 'Positivo' ||
            ws[cell].v === 'Negativo' ||
            ws[cell].v === 'Saldo')
        ) {
          ws[cell].s = resumenFinalStyle;
        }

        // Estilos para valores numéricos
        if (C >= 12 && typeof ws[cell].v === 'number') {
          ws[cell].s =
            ws[cell].v >= 0 ? positiveValueStyle : negativeValueStyle;
        }
      }
    }

    // Ajustar anchos de columna
    ws['!cols'] = headers.map(() => ({ wch: 12 }));

    // Exportar
    XLSX.utils.book_append_sheet(wb, ws, t('Saldos Comparativos'));
    XLSX.writeFile(wb, 'Saldos_Comparativos.xlsx');
  };

  // Total FOB and kilos per client, derived directly from settlements. Kept
  // separate from the saldo aggregation (which is strictly comparison-based).
  // IMPORTANT: this hook MUST be called unconditionally on every render,
  // before the early-return-on-empty-settlements below. Otherwise the hook
  // count changes when settlements toggles between empty and non-empty and
  // React throws "Rendered fewer hooks than expected".
  const clientTotals = useMemo(() => {
    const map = new Map<string, { fob: number; kilos: number }>();
    (settlements || []).forEach((s) => {
      const key = s.receiver;
      const details = s.settlement_details || [];
      const fob = details.reduce((sum, d) => sum + (d.usd_total_fob || 0), 0);
      const kilos = details.reduce((sum, d) => sum + (d.kilos || 0), 0);
      const existing = map.get(key) || { fob: 0, kilos: 0 };
      map.set(key, {
        fob: existing.fob + fob,
        kilos: existing.kilos + kilos,
      });
    });
    return map;
  }, [settlements]);

  if (!settlements || settlements.length === 0) {
    return (
      <div>
        <div className='flex items-center gap-2 mb-3'>
          <h3 className='text-xl font-semibold'>
            {t('Saldos Comparativos')}
          </h3>
        </div>
        <Card className='w-full'>
          <CardBody>
            <p className='text-default-400 text-center py-4'>
              {t('No hay datos disponibles para mostrar')}
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  const saldos = calculateComparativeSaldo;
  const hasSaldos = Object.keys(saldos).length > 0;

  const getColorClass = (value: number) => {
    if (value > 0) return 'text-green-500';
    if (value < 0) return 'text-red-500';
    return '';
  };

  // Calculate saldo per kg for each client
  const getSaldoPerKg = (saldo: number, kilos: number) => {
    if (!kilos || kilos === 0) return null;
    return Number((saldo / kilos).toFixed(2));
  };

  return (
    <div>
      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3'>
        <div className='flex flex-col gap-2'>
          <div className='flex items-center gap-2'>
            <h3 className='text-xl font-semibold'>{t('Saldos Comparativos')}</h3>
            <InformationTooltip>
              <p className='text-sm space-y-2'>
                <span className='font-medium block mb-2'>
                  {t('Cálculo de Saldos Comparativos:')}
                </span>
                <ol className='list-decimal pl-4 space-y-2'>
                  <li>
                    {t('Se filtran las liquidaciones que tienen:')}
                    <ul className='list-disc pl-4 mt-1'>
                      <li>{t('Flete')}</li>
                      <li>{t('Tipo de cambio válido')}</li>
                    </ul>
                  </li>
                  <li>
                    {t(
                      'Se agrupan los detalles por nave y dentro de cada nave:'
                    )}
                    <ul className='list-disc pl-4 mt-1'>
                      <li>{t('Por cliente')}</li>
                      <li>
                        {t('Por producto (variedad-calibre-color-formato)')}
                      </li>
                      <li>
                        {t('Se suman los kilos y FOB total de cada grupo')}
                      </li>
                    </ul>
                  </li>
                  <li>
                    {t('Para cada producto dentro de una nave:')}
                    <ul className='list-disc pl-4 mt-1'>
                      <li>
                        {t(
                          'FOB/kg del cliente = FOB total / kilos del cliente'
                        )}
                      </li>
                      <li>
                        {t(
                          'FOB/kg otros = (FOB total otros clientes) / (kilos otros clientes)'
                        )}
                      </li>
                      <li>
                        {t(
                          'Diferencia = (FOB/kg cliente - FOB/kg otros) × kilos del cliente'
                        )}
                      </li>
                    </ul>
                  </li>
                  <li>
                    {t('El saldo final por cliente:')}
                    <ul className='list-disc pl-4 mt-1'>
                      <li>{t('Positivo: suma de diferencias > 0')}</li>
                      <li>{t('Negativo: suma de diferencias < 0')}</li>
                      <li>{t('Total: positivo + negativo')}</li>
                      <li>
                        {t(
                          '% Comparable: porcentaje de kilos utilizados en la comparación'
                        )}
                      </li>
                      <li>
                        {t(
                          'Saldo/kg: Saldo total dividido por los kilos comparables'
                        )}
                      </li>
                    </ul>
                  </li>
                </ol>
                <p className='mt-4 font-semibold'>
                  {t(
                    'Conclusión: Un saldo positivo significa que el cliente obtuvo mejores precios que el promedio de los demás. Un saldo negativo indica que obtuvo precios por debajo del promedio.'
                  )}
                </p>
              </p>
            </InformationTooltip>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          {hasSaldos && (
            <ExcelExportButton onClick={exportToExcel} />
          )}
        </div>
      </div>
      <Card className='w-full'>
      <CardBody className={!hasSaldos ? '' : 'p-0'}>
        {!hasSaldos ? (
          <p className='text-default-400 text-center py-4'>
            {t('No hay datos comparativos disponibles')}
          </p>
        ) : (
          (() => {
            const rows = Object.entries(saldos)
              .map(([client, clientSaldo]) => {
                const totals = clientTotals.get(client) || {
                  fob: 0,
                  kilos: 0,
                };
                return {
                  client,
                  volume: totals.kilos,
                  fob: totals.fob,
                  positive: clientSaldo.positive || 0,
                  negative: clientSaldo.negative || 0,
                  total: clientSaldo.total || 0,
                  saldoPerKg: getSaldoPerKg(
                    clientSaldo.total,
                    clientSaldo.comparableKilos
                  ),
                  comparable:
                    clientSaldo.totalKilos > 0
                      ? (clientSaldo.comparableKilos /
                          clientSaldo.totalKilos) *
                        100
                      : 0,
                };
              })
              .sort((a, b) => {
                if (!sortDescriptor.column) return 0;
                const col = sortDescriptor.column as string;
                const aVal: number | string =
                  col === 'client' ? a.client : (a as any)[col] ?? -Infinity;
                const bVal: number | string =
                  col === 'client' ? b.client : (b as any)[col] ?? -Infinity;
                let cmp: number;
                if (typeof aVal === 'string' && typeof bVal === 'string') {
                  cmp = aVal.localeCompare(bVal);
                } else {
                  cmp = (aVal as number) - (bVal as number);
                }
                return sortDescriptor.direction === 'descending' ? -cmp : cmp;
              });

            // Aggregate totals for the header row.
            const totalVolume = rows.reduce((s, r) => s + r.volume, 0);
            const totalFob = rows.reduce((s, r) => s + r.fob, 0);
            const totalPos = rows.reduce((s, r) => s + r.positive, 0);
            const totalNeg = rows.reduce((s, r) => s + r.negative, 0);
            const totalSaldo = rows.reduce((s, r) => s + r.total, 0);

            return (
              <Table
                classNames={UNIFIED_TABLE_CLASSNAMES}
                aria-label={t('FOB por Cliente y Variedad')}
                sortDescriptor={sortDescriptor}
                onSortChange={setSortDescriptor}
              >
                <TableHeader>
                  <TableColumn key='client' allowsSorting>
                    {t('CLIENTE')}
                  </TableColumn>
                  <TableColumn key='volume' allowsSorting>
                    {t('VOLUMEN (KG)')}
                  </TableColumn>
                  <TableColumn key='fob' allowsSorting>
                    {t('FOB')}
                  </TableColumn>
                  <TableColumn key='positive' allowsSorting>
                    {t('POSITIVO')}
                  </TableColumn>
                  <TableColumn key='negative' allowsSorting>
                    {t('NEGATIVO')}
                  </TableColumn>
                  <TableColumn key='total' allowsSorting>
                    {t('SALDO')}
                  </TableColumn>
                  <TableColumn key='saldoPerKg' allowsSorting>
                    {t('SALDO/KG')}
                  </TableColumn>
                  <TableColumn key='comparable' allowsSorting>
                    {t('% COMPARABLE')}
                  </TableColumn>
                </TableHeader>
                <TableBody>
                  <TableRow
                    key='__totals__'
                    className='border-b-2 border-default-200 dark:border-default-100 bg-default-50 dark:bg-default-50/20'
                  >
                    <TableCell className='font-bold text-sm'>
                      {t('TOTAL')}
                    </TableCell>
                    <TableCell>
                      <span className='font-bold tabular-nums text-sm flex justify-end'>
                        {formatNumber(totalVolume, { decimals: 0 })}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className='font-bold tabular-nums text-sm flex justify-end'>
                        {formatMoneyUSD(totalFob, { decimals: 0 })}
                      </span>
                    </TableCell>
                    <TableCell className='font-bold'>
                      <ValueDisplay value={totalPos} />
                    </TableCell>
                    <TableCell className='font-bold'>
                      <ValueDisplay value={totalNeg} />
                    </TableCell>
                    <TableCell>
                      <SaldoPill value={totalSaldo} />
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                  </TableRow>
                  {
                    rows.slice(0, visibleCount).map((row) => (
                      <TableRow key={row.client}>
                        <TableCell>
                          <span className='font-medium text-sm'>
                            {row.client}
                          </span>
                        </TableCell>
                        <TableCell>
                          <VolumeCell
                            kilos={row.volume}
                            grandTotalKilos={totalVolume}
                          />
                        </TableCell>
                        <TableCell>
                          <FobCell
                            fob={row.fob}
                            grandTotalFob={totalFob}
                          />
                        </TableCell>
                        <TableCell>
                          <ValueDisplay value={row.positive} size='sm' />
                        </TableCell>
                        <TableCell>
                          <ValueDisplay value={row.negative} size='sm' />
                        </TableCell>
                        <TableCell>
                          <SaldoPill value={row.total} />
                        </TableCell>
                        <TableCell
                          className={`${getColorClass(row.saldoPerKg || 0)} tabular-nums text-sm`}
                        >
                          {row.saldoPerKg !== null
                            ? `${formatNumber(row.saldoPerKg, { decimals: 2 })} USD/kg`
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <ComparableCell value={row.comparable} />
                        </TableCell>
                      </TableRow>
                    )) as any
                  }
                </TableBody>
              </Table>
            );
          })()
        )}
        {(() => {
          const totalRows = Object.keys(saldos).length;
          if (totalRows <= visibleCount) return null;
          return (
            <div className='flex justify-center mt-2'>
              <Button
                variant='light'
                size='sm'
                onPress={() =>
                  setVisibleCount((c) => Math.min(c + 10, totalRows))
                }
                className='w-full'
              >
                {t('Cargar más')} ({totalRows - visibleCount}{' '}
                {t('restantes')})
              </Button>
            </div>
          );
        })()}
      </CardBody>
      </Card>
    </div>
  );
};

export default React.memo(ClientComparativeSaldosTable);
